import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { statusColumn } from './sharedColumns';
import { candidateCellColumn } from './CandidateCell';
import RowActionsCell from './RowActionsCell';
import PipelineDataTable, { candidateShape } from './PipelineDataTable';
import TabEmptyState from './TabEmptyState';
import { registerTab } from './tabRegistry';

// Secondary screening flow — "Scheduling" tab (data-stage="schedule" in the
// mockup). Candidates here have completed first contact and are waiting to
// have an interview slot booked.
//
// NOTE: `details_confirmed` / `waiting_to_schedule` are best-guess field
// names from the mockup's copy — not a confirmed API contract yet.
const SchedulingTab = ({
  candidates,
  isLoading,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  opportunityId,
  assignmentId,
  requirementLocationId,
  stateMachineKey,
  manualActions,
  onActionSuccess,
  onViewProfile,
}) => {
  const columns = useMemo(
    () => [
      candidateCellColumn,
      {
        key: 'details_confirmed',
        header: 'Details confirmed',
        className: 'text-sm',
        tooltip: true,
        render: (_v, row) => row.details_confirmed || '-',
      },
      {
        key: 'waiting_to_schedule',
        header: 'Waiting to schedule',
        className: 'text-sm',
        render: (_v, row) => row.waiting_to_schedule || '-',
      },
      statusColumn,
      {
        key: 'row_actions',
        header: '',
        className: 'text-sm',
        render: (_v, row) => (
          <RowActionsCell
            candidateId={row.candidate_id}
            mappingId={row.id}
            status="schedule"
            opportunityId={opportunityId}
            assignmentId={assignmentId}
            requirementLocationId={requirementLocationId}
            stateMachineKey={stateMachineKey}
            manualActions={manualActions}
            onActionSuccess={onActionSuccess}
            onViewProfile={onViewProfile}
          />
        ),
      },
    ],
    [
      opportunityId,
      assignmentId,
      requirementLocationId,
      stateMachineKey,
      manualActions,
      onActionSuccess,
      onViewProfile,
    ]
  );

  return (
    <PipelineDataTable
      columns={columns}
      candidates={candidates}
      isLoading={isLoading}
      page={page}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      emptyState={
        <TabEmptyState description="No candidates are waiting to be scheduled." />
      }
    />
  );
};

SchedulingTab.propTypes = {
  candidates: PropTypes.arrayOf(candidateShape),
  isLoading: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requirementLocationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  stateMachineKey: PropTypes.string,
  manualActions: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
  ),
  onActionSuccess: PropTypes.func,
  onViewProfile: PropTypes.func,
};

SchedulingTab.defaultProps = {
  candidates: [],
  isLoading: false,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
  onPageChange: () => {},
  onPageSizeChange: () => {},
  opportunityId: undefined,
  assignmentId: undefined,
  requirementLocationId: undefined,
  stateMachineKey: 'secondary',
  manualActions: [],
  onActionSuccess: () => {},
  onViewProfile: () => {},
};

registerTab({
  stateMachineKey: 'secondary',
  state: 'schedule',
  component: SchedulingTab,
});

export default SchedulingTab;
